import { Highlights } from '@api';
import { Badge } from '@design/components/badge/Badge';
import clsx from 'clsx';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useWatchlist } from '../../stores/use-watchlist/useWatchlist';
import { getWatchlistNames } from '../../utils/watchlist-helpers/watchlist-helpers';
import styles from './NewsBadges.module.scss';

export interface NewsBadgesProps {
  highlights?: Highlights[];
  entityTags: string[];
}

export const NewsBadges = ({ highlights, entityTags }: NewsBadgesProps) => {
  const { t } = useTranslation('news');
  const isMostRead = highlights?.includes('most_read');
  const isBreaking = highlights?.includes('breaking');

  const lists = useWatchlist(state => state.lists);

  const watchlistLabel = useMemo(() => {
    const watchlistNames = getWatchlistNames(entityTags, lists);

    if (!watchlistNames.length) {
      return undefined;
    }

    if (watchlistNames.length === 1) {
      return watchlistNames[0];
    }

    return `${watchlistNames[0]} + ${watchlistNames.length - 1}`;
  }, [entityTags, lists]);

  return (
    <div className={styles.root} data-testid="news-badges">
      {isMostRead && (
        <Badge
          data-testid="badge-most-read"
          className={styles.badge}
          size="small"
          badgeStyle="solid"
          color="error"
          leftIcon="Trending"
          label={
            /* i18next-extract-mark-context-next-line ["most_read", "breaking"] */
            t('news:badges.label', { context: 'most_read' })
          }
        />
      )}

      {isBreaking && (
        <Badge
          data-testid="badge-breaking"
          className={clsx(styles.badge, styles.breaking)}
          size="small"
          badgeStyle="solid"
          leftIcon="Breaking"
          label={
            /* i18next-extract-mark-context-next-line ["most_read", "breaking"] */
            t('news:badges.label', { context: 'breaking' })
          }
        />
      )}

      {watchlistLabel && (
        <Badge
          data-testid="badge-watchlist"
          className={clsx(styles.badge, styles.watchlist)}
          size="small"
          badgeStyle="solid"
          leftIcon="Watchlist"
          label={watchlistLabel}
        />
      )}
    </div>
  );
};
